<template>
  <div
    :class="[
      'sidebar-shell relative h-full overflow-hidden',
      sideTheme === 'theme-dark' ? 'sidebar-shell--dark' : 'sidebar-shell--light',
    ]"
  >
    <div class="relative z-10 flex h-full flex-col">
      <div
        v-if="showLogo"
        class="sidebar-logo-container flex h-[56px] w-full shrink-0 items-center justify-center border-b border-dashed bg-white"
        :class="sideTheme === 'theme-dark' ? 'border-white/10' : 'border-[rgba(5,5,5,0.06)]'"
      >
        <transition name="logo-switch" mode="out-in">
          <router-link
            v-if="isCollapse"
            key="logo-collapsed"
            to="/"
            class="flex w-full items-center justify-center no-underline"
          >
            <span
              class="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#1677ff] text-base font-semibold leading-none text-white"
            >
              云
            </span>
          </router-link>
          <router-link
            v-else
            key="logo-expanded"
            to="/"
            class="flex w-full items-center justify-center no-underline"
          >
            <img
              :src="logo"
              alt="雅云科技"
              class="mx-auto block max-h-[44px] w-auto max-w-[190px] object-contain"
            />
          </router-link>
        </transition>
      </div>

      <el-scrollbar
        :class="['sidebar-scroll min-h-0 flex-1 overflow-hidden', sideTheme]"
        wrap-class="scrollbar-wrapper"
      >
        <el-menu
          ref="menuRef"
          :key="menuRenderKey"
          :default-active="activeMenu"
          :default-openeds="defaultOpeneds"
          :collapse="isCollapse"
          :unique-opened="false"
          :collapse-transition="false"
          mode="vertical"
          class="sidebar-menu-antd w-full border-none bg-transparent"
          :class="sideTheme === 'theme-dark' ? 'text-white/85' : 'text-[rgba(0,0,0,0.88)]'"
          :active-text-color="theme"
        >
          <template
            v-for="(route, index) in visibleSidebarRouters"
            :key="route.path + index"
          >
            <div
              v-if="index > 0 && !isCollapse"
              class="sidebar-menu-divider"
              aria-hidden="true"
            />
            <sidebar-item
              :item="route"
              :base-path="route.path"
            />
          </template>
        </el-menu>
      </el-scrollbar>
    </div>
  </div>
</template>

<script setup>
import SidebarItem from "./SidebarItem";
import logo from "@/assets/logo/log.svg";
import useAppStore from "@/store/modules/app";
import useSettingsStore from "@/store/modules/settings";
import usePermissionStore from "@/store/modules/permission";
import { collectSubMenuOpenKeys } from "./menuUtils";

const route = useRoute();
const appStore = useAppStore();
const settingsStore = useSettingsStore();
const permissionStore = usePermissionStore();
const menuRef = ref(null);

const sidebarRouters = computed(() => permissionStore.sidebarRouters);
const visibleSidebarRouters = computed(() =>
  sidebarRouters.value.filter((item) => !item.hidden),
);
const showLogo = computed(() => settingsStore.sidebarLogo);
const sideTheme = computed(() => settingsStore.sideTheme);
const theme = computed(() => settingsStore.theme);
const isCollapse = computed(() => !appStore.sidebar.opened);

const defaultOpeneds = computed(() =>
  collectSubMenuOpenKeys(sidebarRouters.value),
);

const menuRenderKey = computed(
  () => `${isCollapse.value ? "c" : "e"}-${defaultOpeneds.value.join("|")}`,
);

const activeMenu = computed(() => {
  const { meta, path } = route;
  if (meta.activeMenu) {
    return meta.activeMenu;
  }
  return path;
});

function expandAllSubMenus() {
  if (isCollapse.value) {
    return;
  }

  nextTick(() => {
    const menu = menuRef.value;
    if (!menu?.open) {
      return;
    }

    defaultOpeneds.value.forEach((index) => {
      menu.open(index);
    });
  });
}

watch(
  [sidebarRouters, isCollapse],
  () => {
    expandAllSubMenus();
  },
  { deep: true, flush: "post" },
);

onMounted(() => {
  expandAllSubMenus();
});
</script>

<style scoped>
.sidebar-shell--light {
  background: #fff;
  border-right: 1px solid rgba(5, 5, 5, 0.06);
}

.sidebar-shell--dark {
  background: #001529;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.sidebar-logo-container {
  background: #fff !important;
}

.logo-switch-enter-active,
.logo-switch-leave-active {
  transition: all 0.2s ease-in-out;
}

.logo-switch-enter-from,
.logo-switch-leave-to {
  opacity: 0;
  transform: translateY(2px) scale(0.98);
}

.sidebar-menu-divider {
  height: 0;
  margin: 4px 16px;
  border-top: 1px dashed rgba(5, 5, 5, 0.06);
}

.sidebar-shell--dark .sidebar-menu-divider {
  border-top-color: rgba(255, 255, 255, 0.12);
}

:deep(.sidebar-menu-antd.el-menu) {
  padding: 4px 8px 12px !important;
  border-right: none;
}

:deep(.sidebar-menu-antd .el-menu-item),
:deep(.sidebar-menu-antd .el-sub-menu__title) {
  height: 40px !important;
  line-height: 40px !important;
  margin: 2px 0;
  padding-inline: 12px !important;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.88);
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

:deep(.sidebar-menu-antd .el-menu-item:hover),
:deep(.sidebar-menu-antd .el-sub-menu__title:hover) {
  background: rgba(0, 0, 0, 0.04) !important;
  color: rgba(0, 0, 0, 0.88) !important;
}

:deep(.sidebar-menu-antd .el-menu-item.is-active) {
  background: #e6f4ff !important;
  color: #1677ff !important;
  font-weight: 500;
  box-shadow: none;
}

:deep(.sidebar-menu-antd .el-menu-item.is-active::before) {
  display: none;
}

:deep(.sidebar-menu-antd .el-sub-menu .el-menu) {
  background: transparent !important;
  padding: 2px 0 4px;
}

:deep(.sidebar-menu-antd .el-sub-menu .el-menu-item) {
  min-width: auto !important;
  height: 36px !important;
  line-height: 36px !important;
  padding-left: 40px !important;
  margin: 1px 0;
  border-radius: 6px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
}

:deep(.sidebar-menu-antd .el-sub-menu .el-menu-item:hover) {
  color: rgba(0, 0, 0, 0.88) !important;
}

:deep(.sidebar-menu-antd .el-sub-menu .el-menu-item.is-active) {
  background: #e6f4ff !important;
  color: #1677ff !important;
}

:deep(.sidebar-menu-antd .el-sub-menu__title) {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.65);
}

:deep(.sidebar-menu-antd .el-sub-menu.is-active > .el-sub-menu__title),
:deep(.sidebar-menu-antd .el-sub-menu.is-opened > .el-sub-menu__title) {
  color: rgba(0, 0, 0, 0.88);
}

:deep(.sidebar-menu-antd .el-menu-item .svg-icon),
:deep(.sidebar-menu-antd .el-sub-menu__title .svg-icon) {
  margin-right: 10px;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.45);
  transition: color 0.2s ease;
}

:deep(.sidebar-menu-antd .el-menu-item.is-active .svg-icon),
:deep(.sidebar-menu-antd .el-sub-menu.is-active > .el-sub-menu__title .svg-icon) {
  color: #1677ff;
}

:deep(.sidebar-menu-antd .el-sub-menu__icon-arrow) {
  right: 12px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

/* 暗色主题 */
.sidebar-shell--dark :deep(.sidebar-menu-antd .el-menu-item),
.sidebar-shell--dark :deep(.sidebar-menu-antd .el-sub-menu__title) {
  color: rgba(255, 255, 255, 0.85);
}

.sidebar-shell--dark :deep(.sidebar-menu-antd .el-menu-item:hover),
.sidebar-shell--dark :deep(.sidebar-menu-antd .el-sub-menu__title:hover) {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #fff !important;
}

.sidebar-shell--dark :deep(.sidebar-menu-antd .el-menu-item.is-active) {
  background: #1677ff !important;
  color: #fff !important;
}

.sidebar-shell--dark :deep(.sidebar-menu-antd .el-sub-menu .el-menu-item) {
  color: rgba(255, 255, 255, 0.65);
}

.sidebar-shell--dark :deep(.sidebar-menu-antd .el-sub-menu .el-menu-item.is-active) {
  background: #1677ff !important;
  color: #fff !important;
}

.sidebar-shell--dark :deep(.sidebar-menu-antd .el-menu-item .svg-icon),
.sidebar-shell--dark :deep(.sidebar-menu-antd .el-sub-menu__title .svg-icon) {
  color: rgba(255, 255, 255, 0.55);
}

.sidebar-shell--dark :deep(.sidebar-menu-antd .el-menu-item.is-active .svg-icon) {
  color: #fff;
}
</style>
